import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  EditOutlined as EditIcon,
  LinkOffOutlined as UnassignIcon,
  LocationOnOutlined as LocationIcon,
  MapOutlined as DistanceIcon,
  Inventory2Outlined as MaterialsIcon,
  RouteOutlined as RouteIcon,
  LocalShippingOutlined as TruckIcon,
  SearchOffOutlined as NotFoundIcon,
  TripOriginOutlined as StartFacilityIcon,
  FlagOutlined as EndFacilityIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import AssignRouteTruckDialog from '../../components/transportation/AssignRouteTruckDialog';
import RouteBuilderMap from '../../components/transportation/RouteBuilderMap';
import CollectionPointDetailsDialog from '../../components/collection-points/CollectionPointDetailsDialog';
import LabelChips from '../../components/common/LabelChips';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import PageHeader from '../../components/PageHeader';
import { APP_SHAPE } from '../../constants/shape';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { useRoutedPath } from '../../hooks/useRoutedPath';
import { listCollectionPoints } from '../../services/collection-point.service';
import { listSuppliers } from '../../services/supplier.service';
import { getAllWasteCatalogMaterials } from '../../services/waste-catalog.service';
import { useOrganizationStore } from '../../stores/organization.store';
import { useRouteStore } from '../../stores/route.store';
import { useTruckStore } from '../../stores/transportation.store';
import type { RouteStop } from '../../types/route.type';
import type { SupplierCollectionPoint } from '../../types/supplier.type';
import { getRouteColor } from '../../utils/routeColors';

function MetricCard({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: `${APP_SHAPE.radiusLg}px`, height: '100%' }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ color: 'primary.main', display: 'flex', '& svg': { fontSize: 26 } }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function RouteDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { routeId } = useParams<{ routeId: string }>();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const {
    selectedRoute,
    selectedRouteNotFound,
    isLoading,
    isSaving,
    error,
    clearError,
    fetchRoute,
    assignTruck,
    unassignTruck,
  } = useRouteStore();
  const { trucks, trucksLoading, fetchTrucks } = useTruckStore();
  const { canMutate } = useAuthPermissions();

  const [assignOpen, setAssignOpen] = useState(false);
  const [truckId, setTruckId] = useState('');
  const [routeStops, setRouteStops] = useState<{ point: SupplierCollectionPoint; materialId: string | null }[]>([]);
  const routePoints = routeStops.map((stop) => stop.point);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [supplierNamesById, setSupplierNamesById] = useState<Map<string, string>>(new Map());
  const [materialLabelsById, setMaterialLabelsById] = useState<Map<string, string>>(new Map());
  const [detailsPoint, setDetailsPoint] = useState<SupplierCollectionPoint | null>(null);

  const orgId = selectedOrganization?.id;
  const canEdit = Boolean(orgId && canMutate(`recyclee.${orgId}.*.route.write`));
  const canAssign = Boolean(orgId && canMutate(`recyclee.${orgId}.*.route.assign`));

  useEffect(() => {
    if (orgId && routeId) void fetchRoute(orgId, routeId);
  }, [orgId, routeId, fetchRoute]);

  useEffect(() => {
    if (!orgId || !selectedRoute) return;
    setPointsLoading(true);
    void listCollectionPoints({ organization_id: orgId, limit: 500 })
      .then((result) => {
        const byId = new Map(result.collection_points.map((point) => [point.id, point]));
        // One row per PLANNED stop -- the same point may appear once per waste type.
        const planned: RouteStop[] = selectedRoute.stops?.length
          ? selectedRoute.stops
          : selectedRoute.collection_point_ids.map((id) => ({ collection_point_id: id, material_id: null }));
        setRouteStops(
          planned
            .map((stop) => ({ point: byId.get(stop.collection_point_id), materialId: stop.material_id }))
            .filter((stop): stop is { point: SupplierCollectionPoint; materialId: string | null } => Boolean(stop.point)),
        );
      })
      .catch(() => setRouteStops([]))
      .finally(() => setPointsLoading(false));
  }, [orgId, selectedRoute]);

  // Org-wide suppliers (for each stop's supplier subtext) + the global waste catalog (for the
  // details dialog's material chips) -- fetched once, independently. `allSettled`, not `all`:
  // these two must never take each other down (see the identical fix in
  // `TruckRouteScheduleTab.tsx` -- a `.then()` combining both into one write can let a failure
  // in one silently wipe out data the other already fetched successfully).
  useEffect(() => {
    if (!orgId) return;
    void Promise.allSettled([
      listSuppliers({ organization_id: orgId, limit: 500 }),
      // Unscoped: labels for waste types already on this route.
      getAllWasteCatalogMaterials(),
    ]).then(([suppliersSettled, materialsSettled]) => {
      setSupplierNamesById(
        suppliersSettled.status === 'fulfilled'
          ? new Map(suppliersSettled.value.suppliers.map((supplier) => [supplier.id, supplier.name]))
          : new Map(),
      );
      setMaterialLabelsById(
        materialsSettled.status === 'fulfilled'
          ? new Map(materialsSettled.value.map((option) => [option.value, option.label]))
          : new Map(),
      );
    });
  }, [orgId]);

  const routed = useRoutedPath(routePoints);

  if (!selectedOrganization) return <Alert severity="info">{t('transportation.routes.selectOrganization')}</Alert>;

  if (isLoading && (!selectedRoute || selectedRoute.id !== routeId)) {
    return <LoadingSkeleton variant="cards" filters={false} cardCount={4} />;
  }

  if (selectedRouteNotFound) {
    return (
      <Box>
        <PageHeader title={t('transportation.routes.details.pageTitle')} onBack={() => navigate('/transportation/routes')} backLabel={t('transportation.routes.details.backLabel')} sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }} />
        <Paper variant="outlined" sx={{ p: { xs: 3, sm: 6 }, textAlign: 'center', borderRadius: `${APP_SHAPE.radiusLg}px` }}>
          <NotFoundIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5 }} />
          <Typography variant="h5">{t('transportation.routes.details.notFoundTitle')}</Typography>
          <Button variant="outlined" sx={{ mt: 3 }} onClick={() => navigate('/transportation/routes')}>{t('transportation.routes.details.backLabel')}</Button>
        </Paper>
      </Box>
    );
  }

  if (!selectedRoute || selectedRoute.id !== routeId) {
    return (
      <Box>
        <PageHeader title={t('transportation.routes.details.pageTitle')} onBack={() => navigate('/transportation/routes')} backLabel={t('transportation.routes.details.backLabel')} sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }} />
        <Alert severity="error" action={<Button color="inherit" onClick={() => routeId && void fetchRoute(selectedOrganization.id, routeId)}>{t('suppliers.details.retry')}</Button>}>
          {error || t('transportation.routes.details.loadError')}
        </Alert>
      </Box>
    );
  }

  const route = selectedRoute;
  const routeColor = getRouteColor(route.id);

  const openAssign = () => {
    clearError();
    setTruckId('');
    void fetchTrucks({ organization_id: selectedOrganization.id });
    setAssignOpen(true);
  };

  const submitAssign = async () => {
    if (!truckId) return;
    const result = await assignTruck(selectedOrganization.id, route.id, truckId);
    if (result) setAssignOpen(false);
  };

  const doUnassign = () => void unassignTruck(selectedOrganization.id, route.id);

  return (
    <Box>
      <PageHeader
        icon={<RouteIcon />}
        title={(
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: routeColor, flexShrink: 0 }} />
            <span>{route.name}</span>
          </Stack>
        )}
        subtitle={t('transportation.routes.details.subtitle', { city: route.city })}
        onBack={() => navigate('/transportation/routes')}
        backLabel={t('transportation.routes.details.backLabel')}
        sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}
        actions={canEdit ? (
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/transportation/routes/${route.id}/edit`)}>
            {t('transportation.routes.details.editRoute')}
          </Button>
        ) : undefined}
      />

      {error && !assignOpen && <Alert severity="error" onClose={clearError} sx={{ mb: 3 }}>{error}</Alert>}

      {route.labels.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <LabelChips labels={route.labels} />
        </Box>
      )}

      <Grid container spacing={2} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard icon={<LocationIcon />} label={t('transportation.routes.details.metrics.stops')} value={route.stop_count} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard icon={<MaterialsIcon />} label={t('transportation.routes.details.metrics.wasteTypes')} value={route.material_ids.length} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard icon={<DistanceIcon />} label={t('transportation.routes.details.metrics.distance')} value={`${route.estimated_distance_km} km`} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          {/* Consolidates the old separate "Assigned truck" panel into this one card -- same
              slot the truck metric already occupied, no need for two places to look. Exactly
              one action shows at a time: Unassign when a truck is set, Assign when it isn't. */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: `${APP_SHAPE.radiusLg}px`, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ color: 'primary.main', display: 'flex', '& svg': { fontSize: 26 } }}><TruckIcon /></Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap title={route.assigned_truck?.name}>
                  {route.assigned_truck?.name ?? t('transportation.routes.details.noTruck')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {route.assigned_truck ? `${route.assigned_truck.capacity} kg` : t('transportation.routes.details.metrics.truck')}
                </Typography>
              </Box>
            </Stack>
            {canAssign ? (
              route.assigned_truck ? (
                <Button size="small" color="error" startIcon={<UnassignIcon fontSize="small" />} onClick={doUnassign} sx={{ alignSelf: 'flex-start' }}>
                  {t('transportation.routes.details.unassign')}
                </Button>
              ) : (
                <Button size="small" startIcon={<TruckIcon fontSize="small" />} onClick={openAssign} sx={{ alignSelf: 'flex-start' }}>
                  {t('transportation.routes.details.assignTruck')}
                </Button>
              )
            ) : null}
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard icon={<StartFacilityIcon />} label={t('transportation.routes.details.metrics.startFacility')} value={route.start_facility?.name ?? t('transportation.routes.details.noFacility')} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard icon={<EndFacilityIcon />} label={t('transportation.routes.details.metrics.endFacility')} value={route.end_facility?.name ?? t('transportation.routes.details.noFacility')} />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3, alignItems: 'stretch' }}>
        <Grid item xs={12} md={8}>
          <Paper elevation={1} sx={{ borderRadius: `${APP_SHAPE.radiusLg}px`, overflow: 'hidden', height: '100%' }}>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6">{t('transportation.routes.details.stopsTitle')}</Typography>
            </Box>
            <Divider />
            {pointsLoading ? (
              <Box sx={{ p: 2 }}><LoadingSkeleton variant="table" filters={false} columns={1} rows={4} /></Box>
            ) : (
              <RouteBuilderMap
                selectedPoints={routePoints}
                routedGeometry={routed.geometry}
                routeLoading={routed.loading}
                routeFailed={routed.failed}
                color={routeColor}
                height="calc(100vh - 420px)"
              />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ borderRadius: `${APP_SHAPE.radiusLg}px`, overflow: 'hidden', height: '100%' }}>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 420px)' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={32}>#</TableCell>
                    <TableCell>{t('suppliers.collectionPoints.table.name')}</TableCell>
                    <TableCell>{t('suppliers.collectionPoints.table.supplier')}</TableCell>
                    <TableCell width={40} align="right">{t('suppliers.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {routeStops.map(({ point, materialId }, index) => (
                    <TableRow key={`${point.id}-${materialId ?? 'any'}-${index}`} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap title={point.name}>
                          {point.name}
                        </Typography>
                        {materialId ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={materialLabelsById.get(materialId) ?? materialId}
                            sx={{ mt: 0.25, height: 18, fontSize: '0.65rem' }}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          noWrap
                          title={supplierNamesById.get(point.supplier_id) ?? ''}
                          color={supplierNamesById.get(point.supplier_id) ? 'text.primary' : 'text.secondary'}
                        >
                          {supplierNamesById.get(point.supplier_id) ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('suppliers.table.view')}>
                          <IconButton
                            size="small"
                            aria-label={`${t('suppliers.table.view')} ${point.name}`}
                            onClick={() => setDetailsPoint(point)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <AssignRouteTruckDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        loading={isSaving || trucksLoading}
        routeName={route.name}
        availableTrucks={trucks}
        truckId={truckId}
        onTruckIdChange={setTruckId}
        onSubmit={() => void submitAssign()}
      />

      <CollectionPointDetailsDialog
        open={detailsPoint !== null}
        point={detailsPoint}
        supplierName={detailsPoint ? supplierNamesById.get(detailsPoint.supplier_id) : undefined}
        materialLabelsById={materialLabelsById}
        onClose={() => setDetailsPoint(null)}
      />
    </Box>
  );
}
